// Sentry browser SDK — opt-in via VITE_SENTRY_ENABLED + VITE_SENTRY_DSN.
//
// Dynamic import puts @sentry/react into a separate Vite chunk, so when the
// feature is disabled the operator browser never downloads the SDK code.
// PII scrubbing patterns mirror backend/app/core/observability.py and
// relay/internal/observability/sentry.go. See deploy/sentry/pii-rules.md
// for the canonical list.

const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g
const ACCESS_KEY_RE = /\b[A-Z0-9]{12}\b/g
const PHONE_RE = /\+\d{7,15}\b/g
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'x-api-key',
  'x-internal-token',
  'proxy-authorization',
])

function scrubString(value: string): string {
  let out = value.replace(JWT_RE, '[REDACTED:JWT]')
  // Access key: 12 chars uppercase alnum, but only if it has at least 2
  // letters — UUID hex tails like "446655440000" must NOT match.
  out = out.replace(ACCESS_KEY_RE, (match) => {
    let letters = 0
    for (let i = 0; i < match.length; i++) {
      const code = match.charCodeAt(i)
      if (code >= 65 && code <= 90) {
        letters++
        if (letters >= 2) return '[REDACTED:ACCESS_KEY]'
      }
    }
    return match
  })
  out = out.replace(PHONE_RE, '[REDACTED:PHONE]')
  return out
}

// Field shape mirrors deploy/sentry/pii-rules.md scrub-table — keep in sync
// when @sentry/react adds new event fields.
type ScrubbableEvent = {
  request?: {
    headers?: Record<string, string>
    query_string?: string
    cookies?: unknown
  }
  message?: unknown
  extra?: Record<string, unknown>
  breadcrumbs?: Array<{
    message?: unknown
    data?: Record<string, unknown>
  } | null>
  exception?: {
    values?: Array<{ value?: unknown }>
  }
}

function scrubEvent<T extends Record<string, unknown>>(event: T): T {
  const e = event as ScrubbableEvent

  if (e.request?.headers) {
    for (const key of Object.keys(e.request.headers)) {
      if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
        e.request.headers[key] = '[REDACTED]'
      }
    }
  }
  if (e.request && typeof e.request.query_string === 'string') {
    e.request.query_string = scrubString(e.request.query_string)
  }
  if (e.request && e.request.cookies !== undefined) {
    e.request.cookies = '[REDACTED]'
  }

  if (typeof e.message === 'string') {
    e.message = scrubString(e.message)
  }
  if (e.extra) {
    for (const key of Object.keys(e.extra)) {
      const v = e.extra[key]
      if (typeof v === 'string') {
        e.extra[key] = scrubString(v)
      }
    }
  }
  if (e.breadcrumbs) {
    for (const bc of e.breadcrumbs) {
      if (!bc) continue
      if (typeof bc.message === 'string') {
        bc.message = scrubString(bc.message)
      }
      if (bc.data) {
        for (const key of Object.keys(bc.data)) {
          const v = bc.data[key]
          if (typeof v === 'string') {
            bc.data[key] = scrubString(v)
          }
        }
      }
    }
  }
  if (e.exception?.values) {
    for (const ex of e.exception.values) {
      if (typeof ex.value === 'string') {
        ex.value = scrubString(ex.value)
      }
    }
  }
  return event
}

// captureExceptionFn is set after initSentry() finishes the dynamic import.
// Module-level so that components which want to report errors (most
// importantly ErrorBoundary.componentDidCatch) can call captureException
// without having to import @sentry/react — that would pull the SDK into the
// main bundle and defeat the tree-shaking that keeps the disabled-mode
// payload at zero bytes of Sentry code.
//
// When Sentry is disabled (VITE_SENTRY_ENABLED!=='true' or empty DSN) this
// stays null. captureException() then becomes a silent no-op — callers can
// invoke it unconditionally.
type CaptureFn = (error: unknown, tags?: Record<string, string>) => void
let captureExceptionFn: CaptureFn | null = null

// setGlobalTagFn is installed by initSentry. Sets a tag on the global scope
// so every subsequent captured event carries it. Used to attach user_id at
// login and call_id while a call is active. Setting a tag to null/undefined
// removes it from the global scope (call_id reset on hangup).
type SetTagFn = (key: string, value: string | null | undefined) => void
let setGlobalTagFn: SetTagFn | null = null

/**
 * Report `error` to Sentry if the SDK is initialised, otherwise silently
 * drop the call. Always safe to call — never throws, never imports the SDK
 * synchronously.
 *
 * Optional `tags` are scoped to this one event (Sentry UI filterable).
 * Common tags: `phase` (getUserMedia | audioContext | webrtc), `call_id`,
 * `errorName` (`NotAllowedError`, `NotFoundError`).
 *
 * Used primarily by ErrorBoundary.componentDidCatch and by the explicit
 * media-error try/catch blocks (getUserMedia / AudioContext).
 */
export function captureException(error: unknown, tags?: Record<string, string>): void {
  captureExceptionFn?.(error, tags)
}

/**
 * Attach (or remove, when value is null/undefined) a tag on the global Sentry
 * scope. Every event captured after the call will carry it. Use for stable
 * per-session attributes (`user_id` at login, `call_id` while a call is
 * active). No-op when Sentry is disabled.
 *
 * Privacy: only UUIDs / opaque identifiers — never phone_number, email,
 * sim_number, or any other PII. PII scrubbing in scrubEvent applies to
 * tag values too, but the rule is "don't put PII in tags at all".
 */
export function setSentryTag(key: string, value: string | null | undefined): void {
  setGlobalTagFn?.(key, value)
}

/**
 * Initialize Sentry browser SDK if enabled, otherwise no-op.
 *
 * Safe to await unconditionally from main.tsx — when disabled, no SDK code
 * is loaded thanks to dynamic import (Vite emits a separate chunk).
 */
export async function initSentry(): Promise<void> {
  if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
    return
  }
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    // Console-only — no SDK loaded yet, nowhere to report this.
    console.warn(
      '[sentry] VITE_SENTRY_ENABLED=true but VITE_SENTRY_DSN empty — skipping init',
    )
    return
  }

  const Sentry = await import('@sentry/react')
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    // Session Replay records DOM + click events for 30s before each error
    // event. maskAllText / maskAllInputs / blockAllMedia keep operator
    // chat content, phone numbers, contact names out of the recording —
    // only structure + clicks are stored. sampleRate 0.1 covers normal
    // sessions; replaysOnErrorSampleRate=1.0 guarantees every crash has
    // a replay attached for debugging.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend: (event) => {
      scrubEvent(event as unknown as Record<string, unknown>)
      return event
    },
  })

  // Store the SDK's captureException so callers can report errors without
  // a static import of @sentry/react. The closure keeps Sentry references
  // contained to this chunk — main bundle remains free of SDK code.
  captureExceptionFn = (e, tags) => {
    if (tags) {
      Sentry.withScope((scope) => {
        for (const [k, v] of Object.entries(tags)) scope.setTag(k, v)
        Sentry.captureException(e)
      })
    } else {
      Sentry.captureException(e)
    }
  }

  // setGlobalTag binds the global scope's tag mutation to the SDK. Sentry's
  // Scope.setTag does not support removal explicitly (no removeTag in v8+),
  // so we pass undefined which Sentry treats as "tag absent" in v9.
  setGlobalTagFn = (key, value) => {
    const scope = Sentry.getGlobalScope()
    scope.setTag(key, value == null ? undefined : value)
  }
}

// _internal exposes captureExceptionFn purely for tests; production callers
// must go through captureException(). Resetting between tests is the test's
// responsibility (Vitest does not reset module state by default).
export const _internal = {
  scrubString,
  scrubEvent,
  getCaptureExceptionFn: () => captureExceptionFn,
  setCaptureExceptionFn: (fn: CaptureFn | null) => {
    captureExceptionFn = fn
  },
}
